
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { generateAnonymousId } from '@/utils/anonymousId';
import { useCommentVotes } from './useCommentVotes';
import { useCommentSubmission } from './useCommentSubmission';
import { fetchCardComments } from '@/services/comments/fetch/cardComments';
import { fetchCommentReplies } from '@/services/comments/fetch/commentReplies';
import { useCommentsRealtime } from './useCommentsRealtime';
import { Comment } from '@/types/social';
import { CommentSortOption } from '@/services/comments/types';

export interface CommentFilter {
  type: 'all' | 'verified' | 'anonymous' | 'reported';
  search?: string;
}

// Enhanced CommentWithVotes that extends Comment
export interface CommentWithVotes extends Comment {
  upvotes: number;
  downvotes: number;
  hasUserUpvoted?: boolean;
  hasUserDownvoted?: boolean;
}

interface UseCardCommentsWithRepliesProps {
  cardId: string;
  limit?: number;
  sortBy?: CommentSortOption;
  serverSidePagination?: boolean;
  enableFiltering?: boolean;
  enableSearch?: boolean;
}

export function useCardCommentsWithReplies({
  cardId,
  limit = 10,
  sortBy = 'newest',
  serverSidePagination = false, // Default to client-side for "Load More"
  enableFiltering = true,
  enableSearch = true
}: UseCardCommentsWithRepliesProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<CommentWithVotes[]>([]);
  const [totalComments, setTotalComments] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  
  // Enhanced state for filtering and search
  const [filter, setFilter] = useState<CommentFilter>({ type: 'all' });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  
  // Client-side pagination state
  const [displayedCount, setDisplayedCount] = useState<number>(limit);
  const [allFetchedComments, setAllFetchedComments] = useState<CommentWithVotes[]>([]);
  
  // Reply state management with enhanced features
  const [replies, setReplies] = useState<Record<string, CommentWithVotes[]>>({});
  const [repliesVisible, setRepliesVisible] = useState<Record<string, boolean>>({});
  const [repliesLoading, setRepliesLoading] = useState<Record<string, boolean>>({});
  const [repliesPagination, setRepliesPagination] = useState<Record<string, { page: number; hasMore: boolean }>>({});
  const [replyForm, setReplyForm] = useState<string | null>(null);

  // Get anonymous ID if user is not authenticated
  useEffect(() => {
    const getAnonymousId = async () => {
      if (!user) {
        const id = await generateAnonymousId();
        setAnonymousId(id);
      }
    };
    getAnonymousId();
  }, [user]);

  // Transform Comment to CommentWithVotes
  const transformToCommentWithVotes = (comment: Comment): CommentWithVotes => ({
    ...comment,
    upvotes: comment.upvotes || 0,
    downvotes: comment.downvotes || 0,
    hasUserUpvoted: false,
    hasUserDownvoted: false
  });

  // Enhanced comment fetching
  const loadComments = useCallback(async (resetComments: boolean = true) => {
    if (!cardId) return;
    
    setIsLoading(true);
    
    try {
      // Fetch a larger batch for client-side pagination
      const fetchLimit = serverSidePagination ? limit : Math.max(100, displayedCount + limit);
      
      let result = await fetchCardComments(cardId, fetchLimit, sortBy);
      
      // Transform to CommentWithVotes
      const commentsWithVotes = result.map(transformToCommentWithVotes);
      
      // Apply client-side filtering if not using server-side pagination
      let filteredComments = commentsWithVotes;
      if (!serverSidePagination) {
        filteredComments = applyClientSideFiltering(commentsWithVotes);
      }
      
      if (resetComments) {
        setAllFetchedComments(filteredComments);
        setComments(filteredComments.slice(0, displayedCount));
        setTotalComments(filteredComments.length);
      } else {
        // For load more functionality
        setAllFetchedComments(filteredComments);
        setComments(filteredComments.slice(0, displayedCount));
        setTotalComments(filteredComments.length);
      }
      
    } catch (error) {
      console.error('Error fetching card comments:', error);
      setComments([]);
      setTotalComments(0);
      setAllFetchedComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [cardId, limit, sortBy, serverSidePagination, filter, searchQuery, displayedCount]);

  // Apply client-side filtering
  const applyClientSideFiltering = useCallback((comments: CommentWithVotes[]) => {
    let filtered = [...comments];
    
    // Apply type filter
    if (filter.type !== 'all') {
      filtered = filtered.filter(comment => {
        switch (filter.type) {
          case 'verified':
            return comment.user_id && comment.profile;
          case 'anonymous':
            return !comment.user_id || !comment.profile;
          case 'reported':
            return comment.reported;
          default:
            return true;
        }
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(comment =>
        comment.content.toLowerCase().includes(query) ||
        comment.profile?.name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [filter, searchQuery]);

  // Handle real-time comment updates
  const handleNewComment = useCallback((newComment: Comment) => {
    console.log('Received new comment via real-time:', newComment);
    const commentWithVotes = transformToCommentWithVotes(newComment);
    
    if (newComment.parent_id) {
      // This is a reply
      setReplies(prev => ({
        ...prev,
        [newComment.parent_id!]: [...(prev[newComment.parent_id!] || []), commentWithVotes]
      }));
    } else {
      // This is a top-level comment
      setAllFetchedComments(prev => [commentWithVotes, ...prev]);
      setComments(prev => [commentWithVotes, ...prev]);
      setTotalComments(prev => prev + 1);
    }
  }, []);

  const handleCommentDelete = useCallback((commentId: string) => {
    console.log('Comment deleted via real-time:', commentId);
    setAllFetchedComments(prev => prev.filter(c => c.id !== commentId));
    setComments(prev => prev.filter(c => c.id !== commentId));
    setTotalComments(prev => Math.max(0, prev - 1));
    
    // Also remove from replies
    setReplies(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(parentId => {
        updated[parentId] = updated[parentId].filter(reply => reply.id !== commentId);
      });
      return updated;
    });
  }, []);

  const handleCommentCountChange = useCallback((newCount: number) => {
    console.log('Comment count changed via real-time:', newCount);
    setTotalComments(newCount);
  }, []);

  // Set up real-time subscriptions
  useCommentsRealtime({
    contentId: cardId,
    contentType: 'card',
    onNewComment: handleNewComment,
    onDeleteComment: handleCommentDelete,
    onCountChange: handleCommentCountChange
  });

  // Load comments when dependencies change
  useEffect(() => {
    setDisplayedCount(limit); // Reset displayed count
    loadComments(true);
  }, [cardId, sortBy, filter.type, searchQuery]);

  // Load more comments function
  const loadMoreComments = useCallback(() => {
    const newDisplayedCount = displayedCount + limit;
    setDisplayedCount(newDisplayedCount);
    
    // Update displayed comments from already fetched comments
    const newComments = allFetchedComments.slice(0, newDisplayedCount);
    setComments(newComments);
    
    // If we need more comments than what we've fetched, fetch more
    if (newDisplayedCount > allFetchedComments.length) {
      loadComments(false);
    }
  }, [displayedCount, limit, allFetchedComments, loadComments]);

  // Check if there are more comments to load
  const hasMoreComments = displayedCount < totalComments;

  // Enhanced reply functionality with pagination
  const fetchReplies = useCallback(async (commentId: string, page: number = 1, loadMore: boolean = false) => {
    if (repliesLoading[commentId]) return;
    
    setRepliesLoading(prev => ({ ...prev, [commentId]: true }));
    
    try {
      const repliesPerPage = 5;
      const repliesData = await fetchCommentReplies(commentId, 'card', repliesPerPage, sortBy);
      const repliesWithVotes = repliesData.map(transformToCommentWithVotes);
      
      setReplies(prev => ({
        ...prev,
        [commentId]: loadMore ? [...(prev[commentId] || []), ...repliesWithVotes] : repliesWithVotes
      }));
      
      setRepliesPagination(prev => ({
        ...prev,
        [commentId]: {
          page: loadMore ? (prev[commentId]?.page || 1) + 1 : 1,
          hasMore: repliesWithVotes.length === repliesPerPage
        }
      }));
    } catch (error) {
      console.error('Error fetching replies:', error);
    } finally {
      setRepliesLoading(prev => ({ ...prev, [commentId]: false }));
    }
  }, [sortBy, repliesLoading]);

  const toggleReplies = useCallback((commentId: string) => {
    const isVisible = repliesVisible[commentId];
    
    if (!isVisible && !replies[commentId]) {
      fetchReplies(commentId);
    }
    
    setRepliesVisible(prev => ({ ...prev, [commentId]: !isVisible }));
  }, [repliesVisible, replies, fetchReplies]);

  const loadMoreReplies = useCallback((commentId: string) => {
    fetchReplies(commentId, (repliesPagination[commentId]?.page || 1) + 1, true);
  }, [fetchReplies, repliesPagination]);

  const toggleReplyForm = useCallback((commentId: string | null) => {
    setReplyForm(commentId);
  }, []);

  // Enhanced search functionality
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);
    
    // Debounce search
    setTimeout(() => {
      setDisplayedCount(limit); // Reset to initial limit
      loadComments(true);
      setIsSearching(false);
    }, 300);
  }, [loadComments, limit]);

  // Comment reporting functionality
  const reportComment = useCallback(async (commentId: string, reason: string) => {
    try {
      console.log(`Reporting comment ${commentId} for reason: ${reason}`);
      
      setComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, reported: true } 
            : comment
        )
      );
      
      setReplies(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(parentId => {
          updated[parentId] = updated[parentId].map(reply =>
            reply.id === commentId
              ? { ...reply, reported: true }
              : reply
          );
        });
        return updated;
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error reporting comment:', error);
      return { success: false, error: 'Failed to report comment' };
    }
  }, []);

  // Votes handling
  const {
    userVotes,
    voteCounts,
    upvoteCounts,
    downvoteCounts,
    isVoting,
    handleVote,
    maxVotes,
  } = useCommentVotes({
    comments,
    userId: user?.id || null,
    anonymousId,
    contentType: 'card',
  });

  // Comment submission with enhanced reply support
  const { isSubmitting, handleSubmitComment } = useCommentSubmission({
    contentId: cardId,
    contentType: 'card',
    userId: user?.id || null,
    anonymousId,
    setComments: setComments as React.Dispatch<React.SetStateAction<Comment[]>>,
    setCommentCount: setTotalComments,
    setReplies: setReplies as React.Dispatch<React.SetStateAction<Record<string, Comment[]>>>,
    setReplyForm: toggleReplyForm
  });

  // Pagination handlers for server-side pagination (if enabled)
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    if (serverSidePagination) {
      loadComments(true);
    }
  }, [loadComments, serverSidePagination]);

  return {
    // Core comment data
    comments,
    totalComments,
    isLoading,
    isSubmitting,
    handleSubmitComment,
    
    // Pagination
    currentPage,
    totalPages: Math.ceil(totalComments / limit),
    handlePageChange,
    loadMoreComments,
    hasMoreComments,
    
    // Voting
    userVotes,
    voteCounts,
    upvoteCounts,
    downvoteCounts,
    isVoting,
    handleVote,
    maxVotes,
    
    // Authentication
    isAuthenticated: !!user,
    
    // Reply functionality
    replies,
    repliesVisible,
    repliesLoading,
    repliesPagination,
    replyForm,
    toggleReplies,
    loadMoreReplies,
    toggleReplyForm,
    
    // Enhanced features
    filter,
    setFilter,
    searchQuery,
    handleSearch,
    isSearching,
    reportComment,
    
    // Utilities
    refetch: () => loadComments(true)
  };
}
